import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, 
  DollarSign, 
  Calendar, 
  Building2,
  TrendingUp,
  Shield,
  LogOut,
  UserPlus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle2,
  XCircle,
  Eye,
  Download,
  Filter,
  RefreshCw,
  Mail,
  Phone,
  Clock,
  FileText,
  ChevronLeft,
  ChevronRight,
  X,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { APP_VERSION } from "@/version";

// Tab options
const TABS = [
  { id: "dashboard", label: "Dashboard", icon: TrendingUp },
  { id: "experts", label: "Experts", icon: Users },
  { id: "users", label: "Users", icon: Users },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "inquiries", label: "Enterprise Inquiries", icon: Building2 },
  { id: "contacts", label: "Contact Messages", icon: Mail },
];

// Currency options
const CURRENCIES = ["USD", "EUR", "GBP", "AED", "INR", "SAR", "QAR", "KWD", "BHD", "OMR"];

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [experts, setExperts] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changePwForm, setChangePwForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);
  
  // Dialog states
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  // Temporary password dialog (shown after expert creation)
  const [tempPasswordDialogOpen, setTempPasswordDialogOpen] = useState(false);
  const [tempPasswordData, setTempPasswordData] = useState({ email: "", password: "" });
  const [passwordCopied, setPasswordCopied] = useState(false);

  // New expert form
  const [newExpert, setNewExpert] = useState({
    name: "",
    email: "",
    title: "",
    bio: "",
    expertise: "",
    industries: "",
    hourly_rate: "",
    currency: "USD",
    meeting_link: "",
    verified: false,
  });

  useEffect(() => {
    const token = localStorage.getItem("expertbase_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setRefreshing(true);
      const [statsRes, expertsRes, usersRes, bookingsRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/experts`),
        axios.get(`${API}/admin/users`).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/admin/bookings`).catch(() => ({ data: { bookings: [] } })),
        axios.get(`${API}/admin/inquiries`).catch(() => ({ data: { inquiries: [] } })),
      ]);
      
      setStats(statsRes.data);
      setExperts(expertsRes.data.experts || []);
      setUsers(usersRes.data.users || usersRes.data || []);
      setBookings(bookingsRes.data.bookings || bookingsRes.data || []);
      setInquiries(inquiriesRes.data.inquiries || inquiriesRes.data || []);
      
      // Fetch contacts separately
      try {
        const contactsRes = await axios.get(`${API}/admin/contacts`);
        setContacts(contactsRes.data.contacts || contactsRes.data || []);
      } catch {
        setContacts([]);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("expertbase_admin_token");
        navigate("/admin/login");
      }
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("expertbase_admin_token");
    delete axios.defaults.headers.common["Authorization"];
    navigate("/admin/login");
    toast.success("Logged out successfully");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (changePwForm.new_password !== changePwForm.confirm) {
      toast.error("New passwords don't match"); return;
    }
    if (changePwForm.new_password.length < 8) {
      toast.error("Password must be at least 8 characters"); return;
    }
    setChangingPw(true);
    try {
      await axios.put(`${API}/admin/change-password`, {
        old_password: changePwForm.old_password,
        new_password: changePwForm.new_password
      });
      toast.success("Password changed successfully!");
      setChangePasswordOpen(false);
      setChangePwForm({ old_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPw(false);
    }
  };

  // Expert CRUD operations
  const handleCreateExpert = async () => {
    if (!newExpert.name || !newExpert.email || !newExpert.title || !newExpert.hourly_rate) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const payload = {
        ...newExpert,
        expertise: newExpert.expertise.split(",").map(e => e.trim()).filter(Boolean),
        industries: newExpert.industries.split(",").map(i => i.trim()).filter(Boolean),
        hourly_rate: parseFloat(newExpert.hourly_rate),
      };

      const response = await axios.post(`${API}/admin/experts`, payload);
      // Show temporary password in a copyable dialog (NOT a toast — it vanishes too fast)
      setTempPasswordData({
        email: newExpert.email,
        password: response.data.temporary_password
      });
      setCreateDialogOpen(false);
      setNewExpert({
        name: "", email: "", title: "", bio: "", expertise: "", industries: "",
        hourly_rate: "", currency: "USD", meeting_link: "", verified: false,
      });
      fetchAllData();
      setTempPasswordDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create expert");
    }
  };

  const handleUpdateExpert = async () => {
    if (!selectedExpert) return;

    try {
      await axios.put(`${API}/admin/experts/${selectedExpert.id}`, {
        name: selectedExpert.name,
        title: selectedExpert.title,
        bio: selectedExpert.bio,
        hourly_rate: parseFloat(selectedExpert.hourly_rate),
        currency: selectedExpert.currency || "USD",
        verified: selectedExpert.verified,
        available: selectedExpert.available,
      });
      toast.success("Expert updated successfully");
      setEditDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update expert");
    }
  };

  const handleDeleteExpert = async (expertId) => {
    try {
      await axios.delete(`${API}/admin/experts/${expertId}`);
      toast.success("Expert deleted successfully");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete expert");
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/cancel`);
      toast.success("Booking cancelled successfully");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel booking");
    }
  };

  const handleToggleVerify = async (expert) => {
    try {
      await axios.put(`${API}/admin/experts/${expert.id}/verify?verified=${!expert.verified}`);
      toast.success(`Expert ${expert.verified ? 'unverified' : 'verified'}`);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update verification status");
    }
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(tempPasswordData.password);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  // Export functions
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map(row => 
        headers.map(header => {
          let cell = row[header];
          if (cell === null || cell === undefined) cell = "";
          if (typeof cell === "object") cell = JSON.stringify(cell);
          cell = String(cell).replace(/"/g, '""');
          return `"${cell}"`;
        }).join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success(`Exported ${data.length} records to CSV`);
  };

  // Filter functions
  const filterData = (data, searchFields) => {
    if (!searchTerm) return data;
    return data.filter(item =>
      searchFields.some(field => 
        String(item[field] || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  const filteredExperts = filterData(experts, ["name", "email", "title"]);
  const filteredUsers = filterData(users, ["name", "email"]);
  const filteredBookings = filterData(bookings, ["user_name", "expert_name", "status"]);
  const filteredInquiries = filterData(inquiries, ["company_name", "contact_name", "email"]);
  const filteredContacts = filterData(contacts, ["name", "email", "subject"]);

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="bg-charcoal border-b border-slate-800 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <Shield className="w-8 h-8 text-gold-500" />
              <div>
                <h1 className="font-display text-xl text-gold-gradient">ExpertBase</h1>
                <p className="text-xs text-slate-500">Admin Portal v{APP_VERSION}</p>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={fetchAllData}
              disabled={refreshing}
              className="text-slate-400 hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Link to="/" target="_blank" className="text-slate-400 hover:text-white text-sm">
              View Site →
            </Link>
            <Button 
              variant="ghost"
              onClick={() => setChangePasswordOpen(true)}
              className="text-slate-400 hover:text-white"
              title="Change Password"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Change Password
            </Button>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-6">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6 bg-charcoal p-2 rounded-sm border border-slate-800">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSearchTerm(""); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                activeTab === tab.id 
                  ? "bg-gold-500 text-black" 
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              <button 
                onClick={() => setActiveTab("experts")}
                className="bg-charcoal border border-slate-800 p-5 rounded-sm hover:border-gold-500/50 transition-colors text-left cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="text-sm text-slate-400">Total Experts</span>
                </div>
                <p className="font-display text-3xl">{stats?.total_experts || 0}</p>
              </button>
              <button 
                onClick={() => setActiveTab("users")}
                className="bg-charcoal border border-slate-800 p-5 rounded-sm hover:border-gold-500/50 transition-colors text-left cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-slate-400">Total Users</span>
                </div>
                <p className="font-display text-3xl">{stats?.total_users || 0}</p>
              </button>
              <button 
                onClick={() => setActiveTab("bookings")}
                className="bg-charcoal border border-slate-800 p-5 rounded-sm hover:border-gold-500/50 transition-colors text-left cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-purple-500" />
                  <span className="text-sm text-slate-400">Total Bookings</span>
                </div>
                <p className="font-display text-3xl">{stats?.total_bookings || 0}</p>
              </button>
              <div className="bg-charcoal border border-slate-800 p-5 rounded-sm hover:border-gold-500/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-gold-500" />
                  <span className="text-sm text-slate-400">Total Revenue</span>
                </div>
                <p className="font-display text-3xl">${(stats?.total_revenue || 0).toLocaleString()}</p>
              </div>
              <button 
                onClick={() => setActiveTab("bookings")}
                className="bg-charcoal border border-slate-800 p-5 rounded-sm hover:border-gold-500/50 transition-colors text-left cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-slate-400">Pending</span>
                </div>
                <p className="font-display text-3xl">{stats?.pending_bookings || 0}</p>
              </button>
              <button 
                onClick={() => setActiveTab("inquiries")}
                className="bg-charcoal border border-slate-800 p-5 rounded-sm hover:border-gold-500/50 transition-colors text-left cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-5 h-5 text-cyan-500" />
                  <span className="text-sm text-slate-400">Inquiries</span>
                </div>
                <p className="font-display text-3xl">{stats?.enterprise_inquiries || 0}</p>
              </button>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <button
                onClick={() => { setActiveTab("experts"); setCreateDialogOpen(true); }}
                className="bg-charcoal border border-slate-800 p-6 rounded-sm hover:border-gold-500/50 transition-all text-left group"
              >
                <UserPlus className="w-8 h-8 text-gold-500 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold mb-1">Add New Expert</h3>
                <p className="text-sm text-slate-400">Create a new expert profile</p>
              </button>
              <button
                onClick={() => exportToCSV(experts.map(e => ({
                  Name: e.name, Email: e.email, Title: e.title,
                  Rate: e.hourly_rate, Currency: e.currency || "USD",
                  Verified: e.verified, Sessions: e.total_sessions, Rating: e.rating
                })), "experts")}
                className="bg-charcoal border border-slate-800 p-6 rounded-sm hover:border-gold-500/50 transition-all text-left group"
              >
                <Download className="w-8 h-8 text-blue-500 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold mb-1">Export Experts</h3>
                <p className="text-sm text-slate-400">Download all experts as CSV</p>
              </button>
              <button
                onClick={() => exportToCSV(bookings.map(b => ({
                  Date: b.scheduled_date, Time: b.scheduled_time,
                  Expert: b.expert_name, User: b.user_name,
                  Amount: b.amount, Status: b.status
                })), "bookings")}
                className="bg-charcoal border border-slate-800 p-6 rounded-sm hover:border-gold-500/50 transition-all text-left group"
              >
                <FileText className="w-8 h-8 text-purple-500 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold mb-1">Export Bookings</h3>
                <p className="text-sm text-slate-400">Download all bookings as CSV</p>
              </button>
              <button
                onClick={() => exportToCSV(inquiries.map(i => ({
                  Company: i.company_name, Contact: i.contact_name,
                  Email: i.email, Size: i.company_size, Industry: i.industry,
                  Date: i.created_at
                })), "inquiries")}
                className="bg-charcoal border border-slate-800 p-6 rounded-sm hover:border-gold-500/50 transition-all text-left group"
              >
                <Building2 className="w-8 h-8 text-cyan-500 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold mb-1">Export Inquiries</h3>
                <p className="text-sm text-slate-400">Download enterprise inquiries</p>
              </button>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-charcoal border border-slate-800 rounded-sm">
                <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                  <h3 className="font-semibold">Recent Bookings</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab("bookings")}>
                    View All →
                  </Button>
                </div>
                <div className="divide-y divide-slate-800">
                  {bookings.slice(0, 5).map((booking, i) => (
                    <div key={i} className="p-4 hover:bg-slate-800/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{booking.user_name || "User"}</p>
                          <p className="text-sm text-slate-400">with {booking.expert_name || "Expert"}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-gold-500 font-semibold">${booking.amount}</p>
                          <p className="text-xs text-slate-500">{booking.scheduled_date}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {bookings.length === 0 && (
                    <p className="p-4 text-slate-500 text-center">No bookings yet</p>
                  )}
                </div>
              </div>

              <div className="bg-charcoal border border-slate-800 rounded-sm">
                <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                  <h3 className="font-semibold">Recent Inquiries</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab("inquiries")}>
                    View All →
                  </Button>
                </div>
                <div className="divide-y divide-slate-800">
                  {inquiries.slice(0, 5).map((inquiry, i) => (
                    <div key={i} className="p-4 hover:bg-slate-800/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{inquiry.company_name}</p>
                          <p className="text-sm text-slate-400">{inquiry.contact_name} • {inquiry.email}</p>
                        </div>
                        <span className="text-xs bg-slate-700 px-2 py-1 rounded">{inquiry.company_size}</span>
                      </div>
                    </div>
                  ))}
                  {inquiries.length === 0 && (
                    <p className="p-4 text-slate-500 text-center">No inquiries yet</p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Experts Tab */}
        {activeTab === "experts" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-charcoal border border-slate-800 rounded-sm">
            <div className="p-4 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h2 className="font-display text-xl">Expert Management ({filteredExperts.length})</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search experts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-obsidian border-slate-700 w-64"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(filteredExperts.map(e => ({
                    Name: e.name, Email: e.email, Title: e.title,
                    Rate: e.hourly_rate, Currency: e.currency || "USD",
                    Verified: e.verified, Available: e.available,
                    Sessions: e.total_sessions, Rating: e.rating, Created: e.created_at
                  })), "experts")}
                  className="border-slate-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-gold-500 hover:bg-gold-400 text-black">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Expert
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-charcoal border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-display text-2xl">Add New Expert</DialogTitle>
                      <DialogDescription className="text-slate-400">
                        Create a new expert profile. A secure temporary password will be generated — you'll be able to copy it after creation.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Name *</Label>
                          <Input
                            value={newExpert.name}
                            onChange={(e) => setNewExpert(prev => ({ ...prev, name: e.target.value }))}
                            className="bg-obsidian border-slate-700"
                            placeholder="Expert Name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            value={newExpert.email}
                            onChange={(e) => setNewExpert(prev => ({ ...prev, email: e.target.value }))}
                            className="bg-obsidian border-slate-700"
                            placeholder="expert@email.com"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Title *</Label>
                        <Input
                          value={newExpert.title}
                          onChange={(e) => setNewExpert(prev => ({ ...prev, title: e.target.value }))}
                          className="bg-obsidian border-slate-700"
                          placeholder="e.g., Former VP at Fortune 500, 3x Founder"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Bio</Label>
                        <Textarea
                          value={newExpert.bio}
                          onChange={(e) => setNewExpert(prev => ({ ...prev, bio: e.target.value }))}
                          className="bg-obsidian border-slate-700"
                          rows={3}
                          placeholder="Expert's background and experience..."
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Hourly Rate *</Label>
                          <Input
                            type="number"
                            value={newExpert.hourly_rate}
                            onChange={(e) => setNewExpert(prev => ({ ...prev, hourly_rate: e.target.value }))}
                            className="bg-obsidian border-slate-700"
                            placeholder="500"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Currency</Label>
                          <Select
                            value={newExpert.currency}
                            onValueChange={(val) => setNewExpert(prev => ({ ...prev, currency: val }))}
                          >
                            <SelectTrigger className="bg-obsidian border-slate-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-charcoal border-slate-700">
                              {CURRENCIES.map(c => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Expertise (comma-separated)</Label>
                        <Input
                          value={newExpert.expertise}
                          onChange={(e) => setNewExpert(prev => ({ ...prev, expertise: e.target.value }))}
                          className="bg-obsidian border-slate-700"
                          placeholder="Strategy, Product, Growth"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Industries (comma-separated)</Label>
                        <Input
                          value={newExpert.industries}
                          onChange={(e) => setNewExpert(prev => ({ ...prev, industries: e.target.value }))}
                          className="bg-obsidian border-slate-700"
                          placeholder="FinTech, SaaS, E-Commerce"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Meeting Link</Label>
                        <Input
                          value={newExpert.meeting_link}
                          onChange={(e) => setNewExpert(prev => ({ ...prev, meeting_link: e.target.value }))}
                          className="bg-obsidian border-slate-700"
                          placeholder="https://zoom.us/j/..."
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-obsidian rounded-sm">
                        <Label>Verified Expert</Label>
                        <Switch
                          checked={newExpert.verified}
                          onCheckedChange={(checked) => setNewExpert(prev => ({ ...prev, verified: checked }))}
                        />
                      </div>
                    </div>
                    <DialogFooter className="mt-6">
                      <Button variant="ghost" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleCreateExpert} className="bg-gold-500 hover:bg-gold-400 text-black">
                        Create Expert
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            
            {/* Experts Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-obsidian">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Expert</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Rate</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Sessions</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Rating</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {filteredExperts.map(expert => (
                    <tr key={expert.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gold-500/20 rounded-full flex items-center justify-center">
                            <span className="text-gold-500 font-semibold">
                              {expert.name.split(' ').map(n => n[0]).join('').slice(0,2)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{expert.name}</p>
                            <p className="text-sm text-slate-400">{expert.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-gold-500 font-semibold">
                          {expert.currency || "USD"} {expert.hourly_rate}
                        </span>
                        <span className="text-slate-500">/hr</span>
                      </td>
                      <td className="p-4">{expert.total_sessions || 0}</td>
                      <td className="p-4">
                        <span className="text-gold-500">★</span> {expert.rating?.toFixed(1) || "5.0"}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {expert.verified ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded">
                              <CheckCircle2 className="w-3 h-3" /> Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs bg-slate-700 text-slate-400 px-2 py-1 rounded">
                              Unverified
                            </span>
                          )}
                          {expert.available === false && (
                            <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">Unavailable</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-charcoal border-slate-700">
                            <DropdownMenuItem onClick={() => window.open(`/experts/${expert.id}`, '_blank')}>
                              <Eye className="w-4 h-4 mr-2" /> View Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setSelectedExpert({...expert}); setEditDialogOpen(true); }}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleVerify(expert)}>
                              {expert.verified ? <XCircle className="w-4 h-4 mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                              {expert.verified ? "Unverify" : "Verify"}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => { setItemToDelete({ type: "expert", id: expert.id, name: expert.name }); setDeleteDialogOpen(true); }}
                              className="text-red-400 focus:text-red-400"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredExperts.length === 0 && (
                <p className="text-center py-12 text-slate-500">No experts found</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-charcoal border border-slate-800 rounded-sm">
            <div className="p-4 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h2 className="font-display text-xl">User Management ({filteredUsers.length})</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-obsidian border-slate-700 w-64"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(filteredUsers.map(u => ({
                    Name: u.name, Email: u.email, Company: u.company || "N/A", Type: u.user_type, Created: u.created_at
                  })), "users")}
                  className="border-slate-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-obsidian">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">User</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Company</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Type</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Joined</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {filteredUsers.map((user, i) => (
                    <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <span className="text-blue-500 font-semibold">
                              {(user.name || "U").split(' ').map(n => n[0]).join('').slice(0,2)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{user.name || "Unknown"}</p>
                            <p className="text-sm text-slate-400">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300">{user.company || "—"}</span>
                      </td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded ${
                          user.user_type === 'expert' ? 'bg-gold-500/20 text-gold-500' : 'bg-slate-700 text-slate-300'
                        }`}>
                          {user.user_type || "user"}
                        </span>
                      </td>
                      <td className="p-4 text-slate-400">{user.created_at?.split('T')[0] || "N/A"}</td>
                      <td className="p-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setViewItem(user); setViewDialogOpen(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredUsers.length === 0 && (
                <p className="text-center py-12 text-slate-500">No users found</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-charcoal border border-slate-800 rounded-sm">
            <div className="p-4 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h2 className="font-display text-xl">All Bookings ({filteredBookings.length})</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search bookings..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-obsidian border-slate-700 w-64"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(filteredBookings.map(b => ({
                    ID: b.id, Date: b.scheduled_date, Time: b.scheduled_time,
                    Expert: b.expert_name, User: b.user_name, Duration: b.duration_minutes,
                    Amount: b.amount, Status: b.status, Created: b.created_at
                  })), "bookings")}
                  className="border-slate-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-obsidian">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Date & Time</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Expert</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">User</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Duration</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Amount</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {filteredBookings.map((booking, i) => (
                    <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                      <td className="p-4">
                        <p className="font-medium">{booking.scheduled_date}</p>
                        <p className="text-sm text-slate-400">{booking.scheduled_time}</p>
                      </td>
                      <td className="p-4">{booking.expert_name || "Expert"}</td>
                      <td className="p-4">{booking.user_name || "User"}</td>
                      <td className="p-4">{booking.duration_minutes || 60} min</td>
                      <td className="p-4 text-gold-500 font-semibold">${booking.amount || 0}</td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded ${
                          booking.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                          booking.status === 'confirmed' ? 'bg-blue-500/20 text-blue-500' :
                          booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {booking.status || "pending"}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-charcoal border-slate-700">
                            <DropdownMenuItem onClick={() => { setViewItem(booking); setViewDialogOpen(true); }}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => { setItemToDelete({ type: "booking", id: booking.id, name: `booking for ${booking.user_name}` }); setDeleteDialogOpen(true); }}
                                  className="text-red-400 focus:text-red-400"
                                >
                                  <XCircle className="w-4 h-4 mr-2" /> Cancel Booking
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredBookings.length === 0 && (
                <p className="text-center py-12 text-slate-500">No bookings found</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Enterprise Inquiries Tab */}
        {activeTab === "inquiries" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-charcoal border border-slate-800 rounded-sm">
            <div className="p-4 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h2 className="font-display text-xl">Enterprise Inquiries ({filteredInquiries.length})</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search inquiries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-obsidian border-slate-700 w-64"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(filteredInquiries.map(i => ({
                    Company: i.company_name, Contact: i.contact_name, Email: i.email,
                    Phone: i.phone, Size: i.company_size, Industry: i.industry,
                    Service: i.service_type, Needs: i.needs, Created: i.created_at
                  })), "enterprise_inquiries")}
                  className="border-slate-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
            
            <div className="divide-y divide-slate-800">
              {filteredInquiries.map((inquiry, i) => (
                <div key={i} className="p-6 hover:bg-slate-800/30 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{inquiry.company_name}</h3>
                        <span className="text-xs bg-slate-700 px-2 py-1 rounded">{inquiry.company_size}</span>
                        {inquiry.service_type && (
                          <span className="text-xs bg-gold-500/20 text-gold-500 px-2 py-1 rounded">{inquiry.service_type}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-400 mb-3">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" /> {inquiry.contact_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" /> {inquiry.email}
                        </span>
                        {inquiry.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" /> {inquiry.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" /> {inquiry.industry}
                        </span>
                      </div>
                      {inquiry.needs && (
                        <p className="text-slate-300 bg-obsidian p-3 rounded text-sm">{inquiry.needs}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{inquiry.created_at?.split('T')[0]}</p>
                      <a 
                        href={`mailto:${inquiry.email}`}
                        className="inline-flex items-center gap-1 text-gold-500 hover:text-gold-400 text-sm mt-2"
                      >
                        <Mail className="w-4 h-4" /> Reply
                      </a>
                    </div>
                  </div>
                </div>
              ))}
              {filteredInquiries.length === 0 && (
                <p className="text-center py-12 text-slate-500">No inquiries found</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Contact Messages Tab */}
        {activeTab === "contacts" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-charcoal border border-slate-800 rounded-sm">
            <div className="p-4 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h2 className="font-display text-xl">Contact Messages ({filteredContacts.length})</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search messages..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-obsidian border-slate-700 w-64"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(filteredContacts.map(c => ({
                    Name: c.name, Email: c.email, Subject: c.subject,
                    Message: c.message, Created: c.created_at
                  })), "contact_messages")}
                  className="border-slate-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
            
            <div className="divide-y divide-slate-800">
              {filteredContacts.map((contact, i) => (
                <div key={i} className="p-6 hover:bg-slate-800/30 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold">{contact.name}</h3>
                        <span className="text-slate-400 text-sm">{contact.email}</span>
                      </div>
                      {contact.subject && (
                        <p className="text-gold-500 text-sm mb-2">{contact.subject}</p>
                      )}
                      <p className="text-slate-300 bg-obsidian p-3 rounded text-sm">{contact.message}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{contact.created_at?.split('T')[0]}</p>
                      <a 
                        href={`mailto:${contact.email}`}
                        className="inline-flex items-center gap-1 text-gold-500 hover:text-gold-400 text-sm mt-2"
                      >
                        <Mail className="w-4 h-4" /> Reply
                      </a>
                    </div>
                  </div>
                </div>
              ))}
              {filteredContacts.length === 0 && (
                <p className="text-center py-12 text-slate-500">No contact messages found</p>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Edit Expert Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-charcoal border-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit Expert</DialogTitle>
          </DialogHeader>
          {selectedExpert && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={selectedExpert.name}
                    onChange={(e) => setSelectedExpert(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-obsidian border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={selectedExpert.email} disabled className="bg-obsidian border-slate-700 opacity-50" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={selectedExpert.title}
                  onChange={(e) => setSelectedExpert(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-obsidian border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Bio</Label>
                <Textarea
                  value={selectedExpert.bio || ""}
                  onChange={(e) => setSelectedExpert(prev => ({ ...prev, bio: e.target.value }))}
                  className="bg-obsidian border-slate-700"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hourly Rate</Label>
                  <Input
                    type="number"
                    value={selectedExpert.hourly_rate}
                    onChange={(e) => setSelectedExpert(prev => ({ ...prev, hourly_rate: e.target.value }))}
                    className="bg-obsidian border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select
                    value={selectedExpert.currency || "USD"}
                    onValueChange={(val) => setSelectedExpert(prev => ({ ...prev, currency: val }))}
                  >
                    <SelectTrigger className="bg-obsidian border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-charcoal border-slate-700">
                      {CURRENCIES.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-obsidian rounded-sm">
                <Label>Verified Expert</Label>
                <Switch
                  checked={selectedExpert.verified}
                  onCheckedChange={(checked) => setSelectedExpert(prev => ({ ...prev, verified: checked }))}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-obsidian rounded-sm">
                <Label>Available for Bookings</Label>
                <Switch
                  checked={selectedExpert.available !== false}
                  onCheckedChange={(checked) => setSelectedExpert(prev => ({ ...prev, available: checked }))}
                />
              </div>
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateExpert} className="bg-gold-500 hover:bg-gold-400 text-black">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete/Cancel Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-charcoal border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {itemToDelete?.type === "booking" ? "Cancel Booking" : "Confirm Delete"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {itemToDelete?.type === "booking" 
                ? <>Are you sure you want to cancel <strong className="text-white">{itemToDelete?.name}</strong>? The user will be notified.</>
                : <>Are you sure you want to delete <strong className="text-white">{itemToDelete?.name}</strong>? This action cannot be undone.</>
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)}>Go Back</Button>
            <Button 
              onClick={() => {
                if (itemToDelete?.type === "booking") {
                  handleCancelBooking(itemToDelete?.id);
                } else {
                  handleDeleteExpert(itemToDelete?.id);
                }
              }}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {itemToDelete?.type === "booking" ? "Cancel Booking" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-charcoal border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Details</DialogTitle>
          </DialogHeader>
          {viewItem && (
            <div className="space-y-3 mt-4">
              {Object.entries(viewItem).map(([key, value]) => (
                <div key={key} className="flex justify-between py-2 border-b border-slate-800">
                  <span className="text-slate-400 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="text-white text-right max-w-[60%] truncate">{String(value || "N/A")}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Temporary Password Dialog — shown after expert creation */}
      <Dialog open={tempPasswordDialogOpen} onOpenChange={(open) => {
        if (!open) setPasswordCopied(false);
        setTempPasswordDialogOpen(open);
      }}>
        <DialogContent className="bg-charcoal border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-gold-500">
              ✅ Expert Created Successfully
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Share this temporary password securely with the expert. They should change it on first login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Expert Email</p>
              <p className="font-mono text-sm text-slate-300 bg-obsidian px-3 py-2 rounded-sm">
                {tempPasswordData.email}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Temporary Password</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-lg text-gold-400 bg-obsidian px-3 py-3 rounded-sm tracking-wider text-center">
                  {tempPasswordData.password}
                </code>
                <button
                  onClick={handleCopyPassword}
                  className="px-3 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-sm transition-colors text-sm shrink-0"
                >
                  {passwordCopied ? "✓ Copied!" : "Copy"}
                </button>
              </div>
            </div>
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm">
              <p className="text-amber-400 text-xs">
                ⚠️ This password will not be shown again. Copy it now and share it securely (e.g. via a secure message or email).
              </p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              onClick={() => setTempPasswordDialogOpen(false)}
              className="bg-gold-500 hover:bg-gold-400 text-black w-full"
            >
              I've Saved the Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      {changePasswordOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-charcoal border border-slate-700 rounded-sm w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl text-white">Change Admin Password</h2>
              <button onClick={() => setChangePasswordOpen(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Current Password</label>
                <input
                  type="password"
                  value={changePwForm.old_password}
                  onChange={e => setChangePwForm(p => ({ ...p, old_password: e.target.value }))}
                  className="w-full bg-obsidian border border-slate-700 rounded-sm px-4 py-2 text-white focus:border-gold-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">New Password</label>
                <input
                  type="password"
                  value={changePwForm.new_password}
                  onChange={e => setChangePwForm(p => ({ ...p, new_password: e.target.value }))}
                  className="w-full bg-obsidian border border-slate-700 rounded-sm px-4 py-2 text-white focus:border-gold-500 outline-none"
                  placeholder="8+ chars, uppercase, lowercase, number"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={changePwForm.confirm}
                  onChange={e => setChangePwForm(p => ({ ...p, confirm: e.target.value }))}
                  className="w-full bg-obsidian border border-slate-700 rounded-sm px-4 py-2 text-white focus:border-gold-500 outline-none"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1 border-slate-700"
                  onClick={() => setChangePasswordOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={changingPw}
                  className="flex-1 bg-gold-500 hover:bg-gold-400 text-black font-semibold">
                  {changingPw ? "Saving..." : "Save Password"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
